/**
 * Temu productWhExtAttrReq.productOrigin：产地二字码 + CN 省份 region2Id。
 * 枚举见官方「发品-省份枚举值」（temu-auther.md）。
 * 一级区域目录与卖家后台一致：二字简码 + 中国大陆/香港/澳门/台湾。
 */

import { TEMU_REGION1_OPTIONS } from '@/temu-platform/utils/temuRegion1Options';
import { fixedUi } from '@/i18n/fixedUi';

export { TEMU_REGION1_OPTIONS };

const REGION1_CODE_SET = new Set(TEMU_REGION1_OPTIONS.map((o) => o.value));

const REGION1_LABEL_TO_CODE = (() => {
  const map = new Map();
  TEMU_REGION1_OPTIONS.forEach((o) => {
    const label = String(o.label || '').trim();
    if (label) map.set(label, o.value);
  });
  map.set('中国', 'CN');
  map.set('中华人民共和国', 'CN');
  map.set('China', 'CN');
  map.set('CHINA', 'CN');
  map.set('Hong Kong', 'HK');
  map.set('Macao', 'MO');
  map.set('Macau', 'MO');
  map.set('Taiwan', 'TW');
  map.set('印尼', 'ID');
  return map;
})();

/**
 * 解析一级区域二字简码（产地 / 制造地点共用）。
 * 只接受二字码或目录中文名，不会把「中国大陆」原样下发。
 * @param {unknown} raw
 * @returns {string} 如 CN / HK；无法识别时为空串
 */
export function resolveTemuRegion1ShortName(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';
  const upper = text.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  return REGION1_LABEL_TO_CODE.get(text) || REGION1_LABEL_TO_CODE.get(upper) || '';
}

export function isKnownTemuRegion1Code(code) {
  return REGION1_CODE_SET.has(String(code || '').trim().toUpperCase());
}

/** @type {{ value: string, label: string }[]} */
export const TEMU_CN_REGION2_OPTIONS = [
  { value: '43000000000002', label: '北京市' },
  { value: '43000000000003', label: '安徽省' },
  { value: '43000000000004', label: '福建省' },
  { value: '43000000000005', label: '甘肃省' },
  { value: '43000000000006', label: '广东省' },
  { value: '43000000000007', label: '广西壮族自治区' },
  { value: '43000000000008', label: '贵州省' },
  { value: '43000000000009', label: '海南省' },
  { value: '43000000000010', label: '河北省' },
  { value: '43000000000011', label: '河南省' },
  { value: '43000000000012', label: '黑龙江省' },
  { value: '43000000000013', label: '湖北省' },
  { value: '43000000000014', label: '湖南省' },
  { value: '43000000000015', label: '吉林省' },
  { value: '43000000000016', label: '江苏省' },
  { value: '43000000000017', label: '江西省' },
  { value: '43000000000018', label: '辽宁省' },
  { value: '43000000000019', label: '内蒙古自治区' },
  { value: '43000000000020', label: '宁夏回族自治区' },
  { value: '43000000000021', label: '青海省' },
  { value: '43000000000022', label: '山东省' },
  { value: '43000000000023', label: '山西省' },
  { value: '43000000000024', label: '陕西省' },
  { value: '43000000000025', label: '上海市' },
  { value: '43000000000026', label: '四川省' },
  { value: '43000000000027', label: '天津市' },
  { value: '43000000000028', label: '西藏自治区' },
  { value: '43000000000029', label: '新疆维吾尔自治区' },
  { value: '43000000000030', label: '云南省' },
  { value: '43000000000031', label: '浙江省' },
  { value: '43000000000032', label: '重庆市' },
];

const REGION2_ID_SET = new Set(TEMU_CN_REGION2_OPTIONS.map((o) => o.value));

const REGION2_LABEL_TO_ID = (() => {
  const map = new Map();
  TEMU_CN_REGION2_OPTIONS.forEach((o) => {
    const label = String(o.label || '').trim();
    if (!label) return;
    map.set(label, o.value);
    // 工作台/表格常见短名：广东、北京
    const short = label
      .replace(/(壮族|回族|维吾尔)?自治区$/, '')
      .replace(/省$/, '')
      .replace(/市$/, '');
    if (short && short !== label) map.set(short, o.value);
  });
  return map;
})();

/**
 * 归一化产地二字码。模板/工作台可能写入「中国」「中国大陆」「China」。
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizeTemuOriginRegion(raw) {
  return resolveTemuRegion1ShortName(raw);
}

/**
 * 解析官方 region2Id：支持枚举数字 / 中文省名。
 * @param {unknown} raw
 * @returns {number} 有效 id，无法解析时为 0
 */
export function resolveTemuRegion2Id(raw) {
  if (raw == null) return 0;
  const text = String(raw).trim();
  if (!text) return 0;

  if (REGION2_ID_SET.has(text)) {
    const n = Number(text);
    return Number.isSafeInteger(n) && n > 0 ? n : 0;
  }

  const byLabel = REGION2_LABEL_TO_ID.get(text)
    || REGION2_LABEL_TO_ID.get(text.replace(/省$|市$|自治区$/, ''));
  if (byLabel) {
    const n = Number(byLabel);
    return Number.isSafeInteger(n) && n > 0 ? n : 0;
  }

  // 仅接受官方长枚举；短数字（如测试用 43）不是合法省份 id
  const n = Number(text);
  if (Number.isSafeInteger(n) && n > 0 && REGION2_ID_SET.has(String(n))) {
    return n;
  }
  return 0;
}

/**
 * 组装提交 Temu 的 productOrigin（挂在 productWhExtAttrReq 下）。
 * 官方成功样例用 countryShortName；文档字段为 region1ShortName —— 双写兼容。
 * @param {object} form
 * @returns {{ countryShortName: string, region1ShortName: string, region2Id?: number }}
 */
export function buildTemuProductOrigin(form = {}) {
  const originRegion = normalizeTemuOriginRegion(
    form.productOrigin?.region1ShortName
    || form.productOrigin?.countryShortName
    || form.originRegion
    || form.originCountry
    || 'CN',
  ) || 'CN';

  const region2Id = resolveTemuRegion2Id(
    form.productOrigin?.region2Id
    || form.originRegion2Id
    || form.region2Id,
  );

  if (originRegion === 'CN' && !(region2Id > 0)) {
    throw new Error(fixedUi('产地为中国时请选择省份'));
  }

  return {
    countryShortName: originRegion,
    region1ShortName: originRegion,
    ...(originRegion === 'CN' ? { region2Id } : {}),
  };
}
